import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { PageStrip, enrich, formatBytes, useStreamDailyEvents, useStreams } from '@bloo-team/core-shell'
import { storageRollup, useEventStores } from '../lib/storage'
import { retentionSummary, useStreamConfig } from '../lib/retention'
import { CapacityTab, type CapacityChip } from '../storage/CapacityTab'
import { EventStoresTab } from '../storage/EventStoresTab'
import { RetentionTab } from '../storage/RetentionTab'

/**
 * Datafabric · Storage — capacity, retention and event stores in one place
 * (docs/storage-requirements.md). Absorbs legacy `system/eventstores` (the
 * Event stores tab — saved datasets, not archives: capture A0) and the
 * never-built Lifecycle stub (the Retention tab — `/fabric/lifecycle`
 * redirects here with `?tab=retention`).
 *
 * The strip is ABSOLUTE: used on disk, logical volume, stream and event
 * store counts, the effective retention. No percentage and no ring — there
 * is no denominator until a quota endpoint exists (CB-32), and a bar against
 * an invented total would read as headroom.
 *
 * The tab is URL state (`?tab=`, validated by the app's route shim) so each
 * tab is a shareable link and back/forward walks between them. Stream rows
 * come from the same core-shell hooks the Streams page uses, raw — the
 * capacity tab reads them as the backend reports them. Retention is one
 * cluster-wide value with an optional scope override (`lib/retention.ts`,
 * capture R1GET), so the strip shows the effective value from
 * `useStreamConfig`, amber when any row drifts from it or carries none.
 */

export type StorageTab = 'capacity' | 'retention' | 'stores'
export const STORAGE_TABS: readonly StorageTab[] = ['capacity', 'retention', 'stores'] as const
export const isStorageTab = (v: unknown): v is StorageTab => STORAGE_TABS.includes(v as StorageTab)

export { isCapacityChip, type CapacityChip } from '../storage/CapacityTab'

/**
 * Search params the app's route shim validates (`fabric.storage.tsx`).
 * `chip` pre-filters the capacity tab (Overview's storage checks link
 * `?tab=capacity&chip=no-retention|replicated`); it only means something
 * with the capacity tab and is dropped when the tab changes.
 */
export type FabricStorageParams = { tab?: StorageTab; chip?: CapacityChip }

export interface FabricStoragePageProps extends FabricStorageParams {
  clusterId: string
  scopeId: string
}

const days = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : `${n}d`)

export function FabricStoragePage({ clusterId, scopeId, tab = 'capacity', chip }: FabricStoragePageProps) {
  const navigate = useNavigate()
  const scope = { clusterId, scopeId }

  const streamsQ = useStreams(scope)
  const dailyQ = useStreamDailyEvents(scope)
  const storesQ = useEventStores(scope)
  const config = useStreamConfig(scope)

  const streams = useMemo(() => streamsQ.data?.streams ?? [], [streamsQ.data])
  const enriched = useMemo(() => enrich(streams, undefined, dailyQ.data, undefined), [streams, dailyQ.data])
  const rollup = useMemo(() => storageRollup(streams), [streams])
  const retention = useMemo(
    () => retentionSummary(streams, { cluster: config.cluster, scope: config.scope }),
    [streams, config.cluster, config.scope],
  )
  const stores = useMemo(() => storesQ.data ?? [], [storesQ.data])
  const retentionAttention = retention.streamsDiffering + rollup.noRetention

  const switchTab = (next: StorageTab) => {
    if (next === tab) return
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, tab: next, chip: undefined }),
      replace: false,
    })
  }

  const switchChip = (next: CapacityChip | null) => {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, chip: next ?? undefined }),
      replace: true,
    })
  }

  return (
    <div>
      <PageStrip
        title="Datafabric · Storage"
        stats={[
          { label: 'used on disk', value: formatBytes(rollup.onDisk) },
          { label: 'logical', value: formatBytes(rollup.logical) },
          { label: 'streams', value: String(rollup.streams) },
          { label: 'event stores', value: storesQ.isPending ? '…' : String(stores.length) },
          {
            label: 'retention',
            value: config.isPending ? '…' : days(retention.effective?.retention),
            ...(retentionAttention > 0 ? { severity: 'low' as const } : {}),
          },
        ]}
        meta={streamsQ.isFetching || storesQ.isFetching || config.isFetching ? 'refreshing…' : `scope ${scopeId}`}
      />

      <div className="hud-grid">
        <div className="hud-panel hud-span-12" style={{ padding: 'var(--pnl-pad)' }}>
          <div className="hud-stack">
            <div className="bui-tabs" role="tablist" aria-label="Storage sections">
              <button type="button" role="tab" className="bui-tab" aria-selected={tab === 'capacity'} onClick={() => switchTab('capacity')}>
                Capacity
              </button>
              <button type="button" role="tab" className="bui-tab" aria-selected={tab === 'retention'} onClick={() => switchTab('retention')}>
                Retention
                {retentionAttention > 0 ? <span className="n">{retentionAttention} drift</span> : null}
              </button>
              <button type="button" role="tab" className="bui-tab" aria-selected={tab === 'stores'} onClick={() => switchTab('stores')}>
                Event stores <span className="n">{storesQ.isPending ? '' : stores.length}</span>
              </button>
            </div>

            {tab === 'capacity' ? (
              <CapacityTab
                clusterId={clusterId}
                scopeId={scopeId}
                rows={enriched}
                isPending={streamsQ.isPending}
                isError={streamsQ.isError}
                error={streamsQ.error}
                chip={chip}
                onChipChange={switchChip}
              />
            ) : null}
            {tab === 'retention' ? (
              <RetentionTab clusterId={clusterId} scopeId={scopeId} />
            ) : null}
            {tab === 'stores' ? (
              <EventStoresTab
                clusterId={clusterId}
                scopeId={scopeId}
                stores={stores}
                isPending={storesQ.isPending}
                isError={storesQ.isError}
                error={storesQ.error}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
